import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Countdown from "./Countdown";
import Logo from "./Logo";
import { HERO_IMAGE } from "@/config/site";

const reveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero({ dealEnd, heroImage }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <header ref={ref} id="top" className="relative min-h-screen bg-[#0A0A0A] overflow-hidden">
      <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />

      <nav className="relative z-20 flex items-center justify-between px-5 sm:px-10 py-6">
        <Logo />
        <div className="flex items-center gap-3">
          <a
            href="/account"
            data-testid="account-link"
            className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-white border border-[#333] px-3 py-2 hover:border-[#E60000] hover:text-[#E60000] transition-colors"
          >
            ACCOUNT
          </a>
          <div
            data-testid="drop-badge"
            className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-[#E60000] border border-[#E60000]/40 px-3 py-2"
          >
            2-WEEK DROP
          </div>
        </div>
      </nav>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 pt-8 md:pt-14 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center"
      >
        <div>
          <div className="overflow-hidden">
            <motion.p
              custom={0}
              variants={reveal}
              initial="hidden"
              animate="show"
              data-testid="hero-eyebrow"
              className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase"
            >
              Limited Time Hype Drop - Supreme Socks
            </motion.p>
          </div>

          <h1 className="mt-4" data-testid="deals-hero-title">
            <span className="block overflow-hidden">
              <motion.span
                custom={1}
                variants={reveal}
                initial="hidden"
                animate="show"
                className="block font-display uppercase text-[26vw] lg:text-[12rem] leading-[0.85] tracking-tight text-white"
              >
                DEALS<span className="text-[#E60000]">!</span>
              </motion.span>
            </span>
          </h1>

          <div className="overflow-hidden mt-4">
            <motion.p
              custom={2}
              variants={reveal}
              initial="hidden"
              animate="show"
              data-testid="hero-subheading"
              className="font-mono text-sm md:text-base text-[#999] max-w-xl leading-relaxed"
            >
              Black &amp; white Supreme crew socks. White{" "}
              <span className="text-white">$19</span> - Black{" "}
              <span className="text-white">$20</span> - was{" "}
              <span className="line-through">$25</span>. Shipping not included. When the timer
              dies, the deal dies.
            </motion.p>
          </div>

          <motion.div
            custom={3}
            variants={reveal}
            initial="hidden"
            animate="show"
            className="overflow-hidden mt-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <Countdown dealEnd={dealEnd} />
              <div className="flex gap-3">
                <button
                  onClick={() => scrollTo("drop")}
                  data-testid="hero-shop-btn"
                  className="bg-[#E60000] text-white font-display uppercase tracking-wide text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors"
                >
                  Cop the drop
                </button>
                <button
                  onClick={() => scrollTo("payments")}
                  data-testid="hero-payments-btn"
                  className="border border-[#333] text-white font-display uppercase tracking-wide text-lg px-8 py-4 hover:border-[#E60000] hover:text-[#E60000] transition-colors"
                >
                  How to pay
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="relative">
          <div className="absolute -inset-0 border border-[#E60000]/60 translate-x-4 translate-y-4" />
          <img
            src={heroImage || HERO_IMAGE}
            alt="Supreme socks drop"
            data-testid="hero-image"
            className="relative w-full h-72 lg:h-[520px] object-cover grayscale contrast-125"
          />
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-4 top-8 bg-[#E60000] text-white font-display px-4 py-2 text-xl"
          >
            $19 WHITE
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute -right-3 bottom-10 bg-white text-black font-display px-4 py-2 text-xl"
          >
            $20 BLACK
          </motion.div>
        </motion.div>
      </motion.div>
    </header>
  );
}
